import { datadogLogs } from '@datadog/browser-logs';
import { IS_VIVUS } from 'utils/constants';
import { ROUTES } from 'utils/constants/constants_routes';
import { useEffect } from 'react';
import axiosInstance from 'utils/axiosInstance';
import useFirebase from 'hooks/useFirebase';

interface LexisNexisInstance {
  dnpf: (
    subdomain: string,
    org: string,
    sessionId: string,
    screenId: string,
  ) => void;
}

declare global {
  interface Window {
    ditm3405: LexisNexisInstance;
  }
}

const SUBDOMAIN_LEXIS = import.meta.env.VITE_LEXIS_NEXIS_SUBDOMAIN;
const ORG_URL = import.meta.env.VITE_LEXIS_NEXIS_ORG_URL;

const LEXIS_NEXIS_TOOLKIT_ID = 'lexis-nexis-toolkit';

const LEXIS_NEXIS_TOOLKIT_URL = `/js/di_toolkit.js`;

const LEXIS_NEXIS_SCREEN_ID = {
  [ROUTES.FIRST_LOAN_WELCOME]: '100',
  [ROUTES.FIRST_LOAN_PERSONAL_DATA]: '120',
  [ROUTES.FIRST_LOAN_ADDRESS]: '140',
  [ROUTES.FIRST_LOAN_INCOME]: '160',
  [ROUTES.BEST_OFFER]: '180',
};
const LEXIS_NEXIS_SCREEN_ID_DEFAULT = '200';

export const useLexisNexis = () => {
  const { firebaseData } = useFirebase();
  const isLexisNexisAvailable = IS_VIVUS;

  useEffect(() => {
    const scriptAlreadyExists = document.getElementById(LEXIS_NEXIS_TOOLKIT_ID);

    if (!isLexisNexisAvailable || scriptAlreadyExists) return;

    try {
      const toolkitScript = document.createElement('script');
      toolkitScript.src = LEXIS_NEXIS_TOOLKIT_URL;
      toolkitScript.async = true;
      toolkitScript.defer = true;
      toolkitScript.id = LEXIS_NEXIS_TOOLKIT_ID;
      document.head.appendChild(toolkitScript);
    } catch {}
  }, []);

  const triggerLexisNexisProfiling = async () => {
    let stepToLogInCaseOfError = `1.${window.location.pathname}`;
    try {
      // Session will be generated by the BE
      const { data: sessionId } = await axiosInstance.get(
        '/client/lexis-nexis-session',
      );
      stepToLogInCaseOfError += ` - 2.${sessionId}`;

      const screenId =
        LEXIS_NEXIS_SCREEN_ID[window.location.pathname] ||
        LEXIS_NEXIS_SCREEN_ID_DEFAULT;

      window.ditm3405?.dnpf(ORG_URL, SUBDOMAIN_LEXIS, sessionId, screenId);
      stepToLogInCaseOfError += ' - 3. script dnpf executed';
      await axiosInstance.post('/client/lexis-nexis-session', { sessionId });
      stepToLogInCaseOfError += ' - 4. profiling executed';

      // Manage success logs through firebase to avoid too much messages
      if (firebaseData.REGISTRATION_DATADOG_LOGS_INFO) {
        datadogLogs.logger.info(
          `Lexis Nexis calls finished successfully - screenId: ${screenId} - session: ${sessionId}`,
        );
      }
    } catch {
      datadogLogs.logger.warn(`${stepToLogInCaseOfError} - `);
      // Silent error
    }
  };

  return {
    isLexisNexisAvailable,
    triggerLexisNexisProfiling,
  };
};
